(function (global) {
  'use strict';

  function assertClient(client) {
    if (!client || typeof client.rpc !== 'function') {
      throw new Error('FanScoreGameplayBridge requires an initialized Supabase client.');
    }
  }

  function makeUuid() {
    if (global.crypto && typeof global.crypto.randomUUID === 'function') {
      return global.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
      var random = Math.random() * 16 | 0;
      var value = char === 'x' ? random : ((random & 0x3) | 0x8);
      return value.toString(16);
    });
  }

  function pageUrl() {
    return global.location ? global.location.href : null;
  }

  function normalizeHandle(value) {
    return String(value || '').trim().toLowerCase();
  }

  function storageKey(handle) {
    return 'fanscore:gameplay:' + handle;
  }

  function readStored(handle) {
    try {
      var raw = global.sessionStorage && global.sessionStorage.getItem(storageKey(handle));
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStored(handle, value) {
    try {
      if (!global.sessionStorage) return;
      if (value) {
        global.sessionStorage.setItem(storageKey(handle), JSON.stringify(value));
      } else {
        global.sessionStorage.removeItem(storageKey(handle));
      }
    } catch (error) {
      // Storage is optional; gameplay tracking should still work without it.
    }
  }

  function rpcError(error) {
    var message = error && (error.message || error.details || error.hint);
    return new Error(message || 'FanScore gameplay tracking failed.');
  }

  function FanScoreGameplayBridge(options) {
    options = options || {};
    assertClient(options.client);

    this.client = options.client;
    this.gameHandle = normalizeHandle(options.gameHandle);

    if (!this.gameHandle) {
      throw new Error('FanScoreGameplayBridge requires gameHandle.');
    }

    this.state = readStored(this.gameHandle);
  }

  FanScoreGameplayBridge.prototype._rpc = async function (payload) {
    var response = await this.client.rpc('fs_arcade_game_session', payload);

    if (response.error) {
      throw rpcError(response.error);
    }

    return response.data;
  };

  FanScoreGameplayBridge.prototype.start = async function (options) {
    options = options || {};

    if (this.state && this.state.sessionId) {
      return {
        ok: true,
        restored: true,
        session_id: this.state.sessionId,
        client_session_id: this.state.clientSessionId,
        game_handle: this.gameHandle
      };
    }

    var clientSessionId = makeUuid();

    var data = await this._rpc({
      p_event: 'start',
      p_game_handle: this.gameHandle,
      p_session_id: null,
      p_client_session_id: clientSessionId,
      p_score: null,
      p_result: {},
      p_metadata: options.metadata || {},
      p_page_url: options.pageUrl || pageUrl()
    });

    this.state = {
      sessionId: data.session_id,
      clientSessionId: clientSessionId,
      gameHandle: this.gameHandle,
      startedAt: data.started_at || new Date().toISOString()
    };

    writeStored(this.gameHandle, this.state);

    return data;
  };

  FanScoreGameplayBridge.prototype.complete = async function (options) {
    options = options || {};

    if (!this.state || !this.state.sessionId) {
      throw new Error('No FanScore gameplay session is open. Call start() first.');
    }

    var data = await this._rpc({
      p_event: 'complete',
      p_game_handle: this.gameHandle,
      p_session_id: this.state.sessionId,
      p_client_session_id: this.state.clientSessionId || null,
      p_score: options.score == null ? null : options.score,
      p_result: options.result || {},
      p_metadata: {},
      p_page_url: options.pageUrl || pageUrl()
    });

    writeStored(this.gameHandle, null);
    this.state = null;

    return data;
  };

  FanScoreGameplayBridge.prototype.abandon = async function (options) {
    options = options || {};

    if (!this.state || !this.state.sessionId) {
      return { ok: true, skipped: true, reason: 'no_open_session' };
    }

    var data = await this._rpc({
      p_event: 'abandon',
      p_game_handle: this.gameHandle,
      p_session_id: this.state.sessionId,
      p_client_session_id: this.state.clientSessionId || null,
      p_score: null,
      p_result: options.result || { reason: options.reason || 'abandoned' },
      p_metadata: {},
      p_page_url: options.pageUrl || pageUrl()
    });

    writeStored(this.gameHandle, null);
    this.state = null;

    return data;
  };

  FanScoreGameplayBridge.prototype.resetLocalSession = function () {
    writeStored(this.gameHandle, null);
    this.state = null;
  };

  FanScoreGameplayBridge.prototype.getOpenSession = function () {
    return this.state ? Object.assign({}, this.state) : null;
  };

  global.FanScoreGameplayBridge = FanScoreGameplayBridge;
})(window);
